import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from './config';
import { v4 as uuidv4 } from 'uuid';

// Function to get user's credit profiles
export const getUserCreditProfiles = async (userId) => {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const userCreditsRef = doc(db, 'user_credit_profiles', userId);
    const docSnap = await getDoc(userCreditsRef);

    if (docSnap.exists()) {
      return { success: true, profiles: docSnap.data().profiles || [] };
    } else {
      // If the document doesn't exist, it means the user has no profiles yet.
      return { success: true, profiles: [] };
    }
  } catch (error) {
    console.error('Error getting user credit profiles:', error);
    return { success: false, error: error.message };
  }
};

// Function to add a new credit profile for a user
export const addUserCreditProfile = async (userId, profileData) => {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  const newProfile = {
    id: uuidv4(),
    ...profileData,
    createdAt: new Date().toISOString(),
  };

  try {
    const userCreditsRef = doc(db, 'user_credit_profiles', userId);
    const docSnap = await getDoc(userCreditsRef);

    if (docSnap.exists()) {
      await updateDoc(userCreditsRef, {
        profiles: arrayUnion(newProfile),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Create the document if it doesn't exist
      await setDoc(userCreditsRef, {
        userId,
        profiles: [newProfile],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }
    return { success: true, profile: newProfile };
  } catch (error) {
    console.error('Error adding user credit profile:', error);
    return { success: false, error: error.message };
  }
};

// Function to update an existing credit profile
export const updateUserCreditProfile = async (userId, profileId, updatedData) => {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const userCreditsRef = doc(db, 'user_credit_profiles', userId);
    const docSnap = await getDoc(userCreditsRef);

    if (docSnap.exists()) {
      const profiles = docSnap.data().profiles || [];
      const profileIndex = profiles.findIndex((p) => p.id === profileId);

      if (profileIndex > -1) {
        const updatedProfiles = [...profiles];
        updatedProfiles[profileIndex] = {
          ...updatedProfiles[profileIndex],
          ...updatedData,
          updatedAt: new Date().toISOString(),
        };

        await updateDoc(userCreditsRef, {
          profiles: updatedProfiles,
          updatedAt: serverTimestamp(),
        });
        return { success: true, profiles: updatedProfiles };
      } else {
        return { success: false, error: 'Profile not found' };
      }
    } else {
      return { success: false, error: 'User profile document not found' };
    }
  } catch (error) {
    console.error('Error updating user credit profile:', error);
    return { success: false, error: error.message };
  }
};

// Function to delete a credit profile
export const deleteUserCreditProfile = async (userId, profileId) => {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  if (!userId) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const userCreditsRef = doc(db, 'user_credit_profiles', userId);
    const docSnap = await getDoc(userCreditsRef);

    if (docSnap.exists()) {
      const profiles = docSnap.data().profiles || [];
      const profileToDelete = profiles.find((p) => p.id === profileId);

      if (profileToDelete) {
        await updateDoc(userCreditsRef, {
          profiles: arrayRemove(profileToDelete),
          updatedAt: serverTimestamp(),
        });
        return { success: true };
      } else {
        return { success: false, error: 'Profile not found' };
      }
    } else {
      return { success: false, error: 'User profile document not found' };
    }
  } catch (error) {
    console.error('Error deleting user credit profile:', error);
    return { success: false, error: error.message };
  }
};
